import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, X } from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';
import { announce } from './LiveAnnouncer';

/**
 * BulkActionBar (PRD §4.12, T-36): appears while rows are selected.
 * "נבחרו 4" · actions · "ביטול בחירה". `mat-chrome`, sticky under the top bar
 * on desktop, floating above the tab bar (clear of the QuickCreate button) on
 * phones. A destructive action confirms with the count ("למחוק 4 קריאות?").
 * Ship only actions that have an existing endpoint (NL-05).
 */
export interface BulkAction {
  key: string;
  label: string;
  icon?: ReactNode;
  /** Runs on the current selection; a returned promise shows the busy state. */
  onRun: () => void | Promise<unknown>;
  danger?: boolean;
  /** Confirm first (required for destructive actions). Name the count in the title. */
  confirm?: { title: string; description?: string; confirmLabel: string };
  disabled?: boolean;
}

interface BulkActionBarProps {
  count: number;
  actions: BulkAction[];
  onClear: () => void;
  className?: string;
}

export default function BulkActionBar({ count, actions, onClear, className = '' }: BulkActionBarProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<BulkAction | null>(null);
  const lastCount = useRef(count);

  // Announce the selection size as it changes (a checkbox click is otherwise silent about the total).
  useEffect(() => {
    if (count !== lastCount.current && count > 0) announce(t('list.bulk.selected', { count }));
    lastCount.current = count;
  }, [count, t]);

  if (count <= 0) return null;

  const run = async (action: BulkAction) => {
    setBusy(action.key);
    try {
      await action.onRun();
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div
        role="region"
        aria-label={t('list.bulk.region')}
        className={`bulk-bar mat-chrome flex items-center gap-x-3 gap-y-2 rounded-xl border border-border px-3 py-2 shadow-elevated sm:flex-wrap ${className}`}
      >
        <span className="shrink-0 whitespace-nowrap text-sm font-semibold text-text tabular-nums" aria-live="off">
          {t('list.bulk.selected', { count })}
        </span>
        <span className="h-5 w-px shrink-0 bg-border" aria-hidden="true" />
        <div className="flex min-w-0 items-center gap-2 overflow-x-auto sm:flex-wrap sm:overflow-visible">
          {actions.map((action) => (
            <button
              key={action.key}
              type="button"
              className={`btn btn-sm ${action.danger ? 'btn-ghost text-danger-700 hover:bg-danger-50 hover:text-danger-700' : 'btn-secondary'}`}
              disabled={!!busy || action.disabled}
              aria-busy={busy === action.key || undefined}
              onClick={() => (action.confirm ? setConfirming(action) : void run(action))}
            >
              {busy === action.key ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : action.icon}
              {action.label}
            </button>
          ))}
        </div>
        <button type="button" className="btn btn-ghost btn-sm ms-auto shrink-0" onClick={onClear} disabled={!!busy} aria-label={t('list.bulk.clear')}>
          <X size={14} aria-hidden="true" />
          <span className="hidden sm:inline">{t('list.bulk.clear')}</span>
        </button>
      </div>

      <ConfirmDialog
        open={!!confirming}
        title={confirming?.confirm?.title ?? ''}
        description={confirming?.confirm?.description}
        confirmLabel={confirming?.confirm?.confirmLabel ?? ''}
        danger={!!confirming?.danger}
        loading={!!busy}
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          const action = confirming;
          setConfirming(null);
          if (action) void run(action);
        }}
      />
    </>
  );
}
